import useIsSmall from "@/hooks/useIsSmall";
import { Product, finance, financeKeys } from "@/services/finance";
import { ActionIcon, Alert, Badge, Button, Group, Modal, NumberInput, Select, Skeleton, Stack, Switch, Text, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import classes from "@/modules/finance/finance.module.css";
import { DateField, MoneyInput, accountOptions, faNum, isoToday, rial, useAccounts, useFinance, useFinanceWrite, useTerminals } from "@/modules/finance/shared";

const sheet = (small: boolean) => (small ? { fullScreen: true, radius: 0 } : { size: "lg", centered: true });

export default function Products() {
  const { can } = useFinance();
  const [params, setParams] = useSearchParams();
  const [stockFor, setStockFor] = useState<Product | null>(null);
  const [editing, setEditing] = useState<Product | null>(null);
  const [creating, setCreating] = useState(false);
  const selling = params.get("sale") === "1";
  const query = useQuery({ queryKey: [...financeKeys.all, "products"], queryFn: () => finance.products() });
  const products = query.data?.items ?? [];

  return (
    <>
      <section className={classes.panel}>
        <div className={classes.panelHead}>
          <div><h2>محصولات و موجودی</h2><small>موجودی اولیه فروش نیست؛ موجودی منفی پذیرفته نمی‌شود.</small></div>
          <Group gap={8}>
            {can("inventory.write") && <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>محصول جدید</Button>}
            {can("sales.write") && products.length > 0 && <Button color="violet" onClick={() => setParams({ sale: "1" })}>فروش محصول</Button>}
          </Group>
        </div>
        {query.isLoading ? <Skeleton height={180} /> : query.isError ? <Alert color="red">دریافت محصولات انجام نشد.<Button variant="subtle" onClick={() => query.refetch()}>تلاش دوباره</Button></Alert> : !products.length ? (
          <div className={classes.empty}>هنوز محصولی برای فروش ثبت نکرده‌اید.{can("inventory.write") && <div><Button mt="sm" variant="light" color="violet" onClick={() => setCreating(true)}>افزودن محصول</Button></div>}</div>
        ) : (
          <>
            <div className={`${classes.tableWrap} ${classes.wide}`}>
              <table>
                <thead><tr><th>محصول</th><th>کد</th><th className={classes.num}>قیمت فروش</th><th>موجودی</th><th /></tr></thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td><strong>{p.name}</strong>{!p.active && <Badge ml={6} size="xs" color="gray" variant="light">غیرفعال</Badge>}</td>
                      <td style={{ direction: "ltr", textAlign: "right" }}>{p.code}</td>
                      <td className={classes.num}>{rial(p.priceRial)}</td>
                      <td><StockBadge product={p} /></td>
                      <td><RowActions product={p} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={classes.cards}>
              {products.map((p) => (
                <div key={p.id} className={classes.card}>
                  <div className={classes.cardTop}><strong>{p.name}</strong><span className={classes.amount}>{rial(p.priceRial)}</span></div>
                  <div className={classes.cardMeta}><span>{p.code}</span><StockBadge product={p} /></div>
                  <RowActions product={p} />
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {creating && <ProductForm onClose={() => setCreating(false)} />}
      {editing && <EditProduct product={editing} onClose={() => setEditing(null)} />}
      {stockFor && <StockPurchase product={stockFor} onClose={() => setStockFor(null)} />}
      {selling && <SaleForm products={products.filter((p) => p.active)} onClose={() => { params.delete("sale"); setParams(params, { replace: true }); }} />}
    </>
  );

  function RowActions({ product }: { product: Product }) {
    if (!can("inventory.write")) return null;
    return (
      <Group gap={6} justify="flex-end" wrap="nowrap">
        <Button size="xs" variant="light" color="violet" onClick={() => setStockFor(product)}>ورود کالا</Button>
        <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(product)}>ویرایش</Button>
      </Group>
    );
  }
}

function StockBadge({ product }: { product: Product }) {
  const low = product.quantityOnHand <= product.lowStockThreshold;
  return <Badge variant="light" color={product.quantityOnHand === 0 ? "red" : low ? "orange" : "gray"}>{faNum(product.quantityOnHand)} عدد{low ? " · رو به اتمام" : ""}</Badge>;
}

function ProductForm({ onClose }: { onClose: () => void }) {
  const small = useIsSmall();
  const [v, setV] = useState({ name: "", code: "", price: "", threshold: 2, qty: 0, cost: "", date: isoToday() });
  const save = useFinanceWrite((body: object, key) => finance.createProduct(body, key), { success: "محصول ثبت شد", onSuccess: onClose });
  const valid = v.name.trim() && v.code.trim() && Number(v.price) > 0 && (v.qty === 0 || Number(v.cost) > 0);
  return (
    <Modal opened onClose={onClose} title="محصول جدید" {...sheet(small)}>
      <Stack>
        <div className={classes.fields}>
          <TextInput label="نام محصول" withAsterisk maxLength={70} value={v.name} onChange={(e) => setV({ ...v, name: e.currentTarget.value })} />
          <TextInput label="کد داخلی" withAsterisk placeholder="COLOR-1" styles={{ input: { direction: "ltr", textAlign: "left" } }} value={v.code} onChange={(e) => setV({ ...v, code: e.currentTarget.value })} />
          <MoneyInput label="قیمت فروش" withAsterisk value={v.price} onChange={(price) => setV({ ...v, price })} />
          <NumberInput label="هشدار موجودی کم از" min={0} allowDecimal={false} value={v.threshold} onChange={(n) => setV({ ...v, threshold: Number(n) || 0 })} />
          <NumberInput label="موجودی فعلی" min={0} allowDecimal={false} value={v.qty} onChange={(n) => setV({ ...v, qty: Number(n) || 0 })} />
          <MoneyInput label="بهای خرید هر عدد" withAsterisk={v.qty > 0} value={v.cost} onChange={(cost) => setV({ ...v, cost })} />
          <DateField label="تاریخ موجودی" value={v.date} max={isoToday()} onChange={(date) => setV({ ...v, date })} />
        </div>
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" disabled={!valid} loading={save.pending} onClick={() => save.run({ name: v.name.trim(), code: v.code.trim(), priceRial: v.price, lowStockThreshold: v.threshold, openingQuantity: v.qty, openingUnitCostRial: v.qty ? v.cost : "0", businessDate: v.date })}>ثبت محصول</Button></Group>
      </Stack>
    </Modal>
  );
}

function EditProduct({ product, onClose }: { product: Product; onClose: () => void }) {
  const small = useIsSmall();
  const [v, setV] = useState({ name: product.name, price: product.priceRial, threshold: product.lowStockThreshold, active: product.active });
  const save = useFinanceWrite((body: object, key) => finance.updateProduct(product.id, body, key), { success: "محصول به‌روز شد", onSuccess: onClose });
  return (
    <Modal opened onClose={onClose} title={`ویرایش ${product.name}`} {...sheet(small)}>
      <Stack>
        <TextInput label="نام محصول" value={v.name} onChange={(e) => setV({ ...v, name: e.currentTarget.value })} />
        <MoneyInput label="قیمت فروش" value={v.price} onChange={(price) => setV({ ...v, price })} />
        <NumberInput label="هشدار موجودی کم از" min={0} allowDecimal={false} value={v.threshold} onChange={(n) => setV({ ...v, threshold: Number(n) || 0 })} />
        <Switch label="فعال برای فروش" checked={v.active} onChange={(e) => setV({ ...v, active: e.currentTarget.checked })} />
        <p className={classes.note}>تغییر قیمت، فروش‌های قبلی را عوض نمی‌کند.</p>
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" disabled={!v.name.trim() || !(Number(v.price) > 0)} loading={save.pending} onClick={() => save.run({ name: v.name.trim(), priceRial: v.price, lowStockThreshold: v.threshold, active: v.active, expectedVersion: product.version })}>ذخیره</Button></Group>
      </Stack>
    </Modal>
  );
}

function StockPurchase({ product, onClose }: { product: Product; onClose: () => void }) {
  const small = useIsSmall();
  const accounts = useAccounts();
  const [v, setV] = useState({ qty: 1, cost: "", accountId: "", date: isoToday(), reference: "" });
  const save = useFinanceWrite((body: object, key) => finance.stockPurchase(body, key), { success: "ورود کالا ثبت شد", onSuccess: onClose });
  const total = v.qty * Number(v.cost || 0);
  return (
    <Modal opened onClose={onClose} title={`ورود کالا · ${product.name}`} {...sheet(small)}>
      <Stack>
        <div className={classes.fields}>
          <NumberInput label="تعداد" min={1} allowDecimal={false} value={v.qty} onChange={(n) => setV({ ...v, qty: Number(n) || 0 })} />
          <MoneyInput label="بهای خرید هر عدد" withAsterisk value={v.cost} onChange={(cost) => setV({ ...v, cost })} />
          <Select label="پرداخت از حساب" withAsterisk data={accountOptions(accounts.data)} value={v.accountId} onChange={(a) => setV({ ...v, accountId: a ?? "" })} />
          <DateField label="تاریخ" value={v.date} max={isoToday()} onChange={(date) => setV({ ...v, date })} />
          <TextInput label="تأمین‌کننده یا شماره فاکتور" value={v.reference} onChange={(e) => setV({ ...v, reference: e.currentTarget.value })} />
        </div>
        <Text size="sm">جمع خرید: <b>{rial(total)}</b> · موجودی پس از ثبت: {faNum(product.quantityOnHand + v.qty)} عدد</Text>
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" disabled={!(v.qty > 0 && Number(v.cost) > 0 && v.accountId)} loading={save.pending} onClick={() => save.run({ productId: product.id, quantity: v.qty, unitCostRial: v.cost, accountId: Number(v.accountId), businessDate: v.date, reference: v.reference.trim() || undefined })}>ثبت خرید</Button></Group>
      </Stack>
    </Modal>
  );
}

type Line = { productId: string; qty: number; price: string };

function SaleForm({ products, onClose }: { products: Product[]; onClose: () => void }) {
  const small = useIsSmall();
  const accounts = useAccounts();
  const terminals = useTerminals();
  const first = products[0];
  const [lines, setLines] = useState<Line[]>(first ? [{ productId: String(first.id), qty: 1, price: first.priceRial }] : []);
  const [discount, setDiscount] = useState("0");
  const [method, setMethod] = useState<string>("");
  const [collected, setCollected] = useState<string | null>(null);
  const [date, setDate] = useState(isoToday());
  const [error, setError] = useState("");
  const save = useFinanceWrite((body: object, key) => finance.createSale(body, key), { success: "فروش ثبت شد", onSuccess: onClose });

  const gross = lines.reduce((s, l) => s + l.qty * Number(l.price || 0), 0);
  const net = Math.max(0, gross - Number(discount || 0));
  const paid = collected === null ? net : Number(collected || 0);
  // «t:<id>» is a card reader; it pays into its own clearing account, not straight into the bank.
  const methods = [
    ...accountOptions(accounts.data).map((o) => ({ value: `a:${o.value}`, label: o.label })),
    ...(terminals.data ?? []).map((t) => ({ value: `t:${t.id}`, label: `پوز ${t.name}` })),
  ];
  const product = (id: string) => products.find((p) => String(p.id) === id);

  const submit = () => {
    if (!lines.length || lines.some((l) => !l.productId || l.qty < 1 || !(Number(l.price) > 0))) return setError("محصول، تعداد و قیمت هر ردیف را کامل کنید.");
    const over = lines.find((l) => l.qty > (product(l.productId)?.quantityOnHand ?? 0));
    if (over) return setError(`موجودی ${product(over.productId)?.name} کافی نیست.`);
    if (paid > net) return setError("مبلغ دریافتی از جمع فاکتور بیشتر است.");
    if (paid > 0 && !method) return setError("روش دریافت را انتخاب کنید.");
    setError("");
    const terminal = method.startsWith("t:") ? (terminals.data ?? []).find((t) => `t:${t.id}` === method) : undefined;
    const accountId = terminal ? terminal.destinationAccountId : method.startsWith("a:") ? Number(method.slice(2)) : undefined;
    save.run({
      businessDate: date, discountRial: discount || "0", note: "",
      lines: lines.map((l) => ({ productId: Number(l.productId), quantity: l.qty, unitPriceRial: l.price })),
      accountId: paid > 0 ? accountId : null, terminalId: terminal?.id ?? null, collectedRial: String(paid),
    });
  };

  return (
    <Modal opened onClose={onClose} title="فروش محصول بدون نوبت" {...sheet(small)}>
      <Stack>
        {lines.map((line, i) => (
          <Group key={i} align="flex-end" wrap="nowrap" gap={8}>
            <Select style={{ flex: 2 }} label={i === 0 ? "محصول" : undefined} data={products.map((p) => ({ value: String(p.id), label: `${p.name} (${faNum(p.quantityOnHand)})` }))} value={line.productId} allowDeselect={false}
              onChange={(id) => setLines(lines.map((l, j) => (j === i ? { ...l, productId: id ?? "", price: product(id ?? "")?.priceRial ?? l.price } : l)))} />
            <NumberInput style={{ width: 80 }} label={i === 0 ? "تعداد" : undefined} min={1} allowDecimal={false} value={line.qty} onChange={(n) => setLines(lines.map((l, j) => (j === i ? { ...l, qty: Number(n) || 0 } : l)))} />
            <MoneyInput style={{ flex: 1.4 }} label={i === 0 ? "قیمت واحد" : undefined} value={line.price} onChange={(price) => setLines(lines.map((l, j) => (j === i ? { ...l, price } : l)))} />
            <ActionIcon variant="subtle" color="gray" size="lg" aria-label="حذف ردیف" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))}><IconTrash size={18} /></ActionIcon>
          </Group>
        ))}
        <div><Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={() => first && setLines([...lines, { productId: String(first.id), qty: 1, price: first.priceRial }])}>ردیف دیگر</Button></div>
        <div className={classes.fields}>
          <MoneyInput label="تخفیف" value={discount} onChange={setDiscount} />
          <DateField label="تاریخ فروش" value={date} max={isoToday()} onChange={setDate} />
          <MoneyInput label="دریافت همین حالا" value={collected ?? String(net)} onChange={setCollected} />
          <Select label="روش دریافت" withAsterisk={paid > 0} placeholder="نقد، بانک یا پوز" data={methods} value={method} onChange={(m) => setMethod(m ?? "")} />
        </div>
        <div className={classes.note}>
          جمع: {rial(gross)} · تخفیف: {rial(discount)} · <b>مبلغ نهایی: {rial(net)}</b><br />
          {net - paid > 0 ? `مانده دریافت‌نشده: ${rial(net - paid)}` : "کامل دریافت می‌شود."}
        </div>
        {error && <Text c="red" size="sm" role="alert">{error}</Text>}
        <Group justify="flex-end"><Button variant="default" onClick={onClose}>انصراف</Button><Button color="violet" loading={save.pending} onClick={submit}>ثبت فروش</Button></Group>
      </Stack>
    </Modal>
  );
}

